import React from 'react';
import bemBlock from '@gik/core/utils/bemBlock';
import IWordpressGrubhubService from '@gik/core/models/wordpress/services/WordpressGrubhubService';
import { openExternalLink } from '@gik/core/utils/browser';
import moment from 'moment';
import { ICalendarEntry, ICalendarEvent } from '@gik/calendar/models/Calendar';
import { grubhubCanOrderNow } from '@gik/calendar/constants/GrubhubConstants';
import { getRecipientContactInformation } from '@gik/calendar/utils/getRecipientContactInformation';
import { useInkindStore } from '@gik/inkind/store/InkindStore';
import { useInkind } from '@gik/api/inkinds/inkind';
import { CopyAddressButton } from '@gik/ui';
import GrubhubButton from '@gik/calendar/components/GrubhubButton/GrubhubButton';

interface GrubhubBoxInfoProps extends React.HTMLAttributes<HTMLDivElement> {
  entry: ICalendarEntry;
  event: ICalendarEvent;
  service: IWordpressGrubhubService;
}

export function GrubhubInfoBox({
  className,
  entry,
  event,
  service,
  ...otherProps
}: GrubhubBoxInfoProps): React.ReactElement {
  const bem = bemBlock('grubhub-info-box');

  const inkindRouteId = useInkindStore(state => state.inkindRouteId);
  const { data: inkind } = useInkind(inkindRouteId);

  const recipientContactInformation = getRecipientContactInformation(inkind);

  const grubhubUrl = service.acf.container_module_grubhub?.service_url_template;
  const logoImage = service.acf.container_module_grubhub?.logo_image;
  const canOrderNow = grubhubCanOrderNow(moment.utc(event.startsAt));
  const isEditing = !!entry.claim;

  const { address1, address2, cityAndState, postcode, fullName } = recipientContactInformation;
  const recipientAddressLine1 = [address1, address2].filter(v => v).join(', ');
  const recipientAddressLine2 = [cityAndState, postcode].join(' ');

  function renderOrderNow() {
    return (
      <div className={bem('order-box')}>
        <header>
          You can order from any delivery service you’d prefer, or <strong>order up to a week in advance</strong>{' '}
          through our partner <strong>GrubHub</strong>
        </header>
        <main>We’ll provide delivery address once you’re all signed up.</main>
      </div>
    );
  }

  function renderOrderLater() {
    return (
      <div className={bem('order-box')}>
        <header>
          You can order from any delivery service you’d prefer, or <strong>order up to a week in advance</strong>{' '}
          through our partner <strong>GrubHub</strong>
        </header>
        <main>We’ll remind you a week prior to order, and provide delivery address after signing up.</main>
      </div>
    );
  }

  function renderEditingClaim() {
    return (
      <div className={bem('edit-claim')}>
        <p>
          <strong>Haven’t ordered yet?</strong> <br />
          Order up to seven days in advance of the claim date, using the provided address:
        </p>
        <address>
          <strong>{fullName}</strong>
          <br />
          {recipientAddressLine1}
          <br />
          {recipientAddressLine2}
        </address>
        <CopyAddressButton textToCopy={[recipientAddressLine1, recipientAddressLine2].join('\r\n')} />
        <br />
        <GrubhubButton url={grubhubUrl} canOrderNow={canOrderNow} />
      </div>
    );
  }

  return (
    <div className={bem(null, ['provide-myself'], className)} {...otherProps}>
      {isEditing ? renderEditingClaim() : canOrderNow ? renderOrderNow() : renderOrderLater()}
    </div>
  );
}
